import { ComponentTextSmall, Modal } from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { useAnalytics } from '@modernhealth/web-analytics';
import Select from 'react-select';

import { locales, SupportedLocale } from '../intl/locales';
import { useLocale } from '../intl/translations';

interface LocaleOption {
  locale: SupportedLocale;
  displayName: string;
}

const localeOptions: readonly LocaleOption[] = locales.map(({ locale, displayName }) => ({
  displayName,
  locale,
}));

/**
 * This select implementation changes the user's locale
 */
export const LocaleSelector: React.FC = () => {
  const { locale, setLocale } = useLocale();
  const analytics = useAnalytics();

  return (
    <Select
      id="localeSelector"
      getOptionValue={option => option.locale}
      getOptionLabel={option => option.displayName}
      defaultValue={localeOptions[0]}
      value={localeOptions.find(option => option.locale === locale)}
      options={localeOptions}
      onChange={option => {
        if (option) {
          analytics.trackClick('Circles: Change Language', {
            locale: option.locale,
            localeDisplayName: option.displayName,
          });

          setLocale(option.locale);
        }
      }}
      formatOptionLabel={({ displayName }) => (
        <ComponentTextSmall>{displayName}</ComponentTextSmall>
      )}
      styles={{
        control(styles) {
          return {
            ...styles,
            minHeight: 30,
            border: 'none',
          };
        },
        singleValue(styles) {
          return {
            ...styles,
            color: colors.gray85,
          };
        },
        dropdownIndicator(styles) {
          return {
            ...styles,
            color: colors.gray40,
          };
        },
        menu(styles) {
          return {
            ...styles,
            width: 'max-content',
            right: 0,
          };
        },
      }}
    />
  );
};

/**
 * This modal is used to change the user's locale
 */
export const LocaleSelectionModal: React.FC<{ isVisible: boolean; close: () => void }> = ({
  isVisible,
  close,
}) => {
  const analytics = useAnalytics();
  const { locale, setLocale } = useLocale();

  // TODO: Don't render the modal when not visible
  // until https://joinmodernhealth.atlassian.net/browse/CIRCLE-501
  // is fixed.
  if (!isVisible) return null;

  return (
    <Modal isVisible={isVisible} onCloseButtonClick={close}>
      <Select
        id="localeSelectorMobile"
        getOptionValue={option => option.locale}
        getOptionLabel={option => option.displayName}
        defaultValue={localeOptions[0]}
        value={localeOptions.find(option => option.locale === locale)}
        options={localeOptions}
        onChange={option => {
          if (option) {
            analytics.trackClick('Circles: Change Language', {
              locale: option.locale,
              localeDisplayName: option.displayName,
            });

            setLocale(option.locale);
          }
        }}
        formatOptionLabel={({ displayName }) => (
          <ComponentTextSmall>{displayName}</ComponentTextSmall>
        )}
        styles={{
          control(styles) {
            return {
              ...styles,
              margin: 10,
              minHeight: 30,
              border: 'none',
            };
          },
          menu() {
            return {
              // TODO: Improve the styling of view on mobile.
              // See https://joinmodernhealth.atlassian.net/browse/CIRCLE-266
              margin: 10,
            };
          },
        }}
      />
    </Modal>
  );
};
